import { clsx, type ClassValue } from "clsx";
import { File } from "lucide-react";
import moment from "moment";
import { twMerge } from "tailwind-merge";
import { z } from "zod";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export const getRequiredFields = (schema: any, prefix = ""): string[] => {
  if (!schema) return [];

  // 1. Unwrap ZodEffects (created by .refine or .superRefine)
  if (schema instanceof z.ZodEffects) {
    return getRequiredFields(schema._def.schema, prefix);
  }

  // 2. If current schema is ZodObject, iterate over its shape properties
  if (schema instanceof z.ZodObject) {
    return Object.entries(schema.shape).flatMap(([key, field]) => {
      const fieldName = prefix ? `${prefix}.${key}` : key;
      return getRequiredFields(field, fieldName);
    });
  }

  // 3. If current schema is ZodArray – recurse inside array element type
  if (schema instanceof z.ZodArray) {
    return getRequiredFields(schema._def.type, prefix);
  }

  // 4. Handle standard type unions (like your date fields z.union([z.string(), z.date()]))
  if (schema instanceof z.ZodUnion) {
    // If ANY branch of the union is optional, the entire field is considered optional
    const leavesOptional = schema._def.options.map((opt: any) => getRequiredFields(opt, prefix));
    if (leavesOptional.some((res: string[]) => res.length === 0)) {
      return [];
    }
    return [prefix];
  }

  // 5. Explicitly identify optional wrapper instances
  const isOptionalWrapper = 
    schema instanceof z.ZodOptional || 
    schema instanceof z.ZodNullable;

  if (isOptionalWrapper) {
    return [];
  }

  // 6. If it's a base primitive node and hasn't been caught by optional wrappers, it is required
  const isPrimitiveType =
    schema instanceof z.ZodString ||
    schema instanceof z.ZodNumber ||
    schema instanceof z.ZodDate ||
    schema instanceof z.ZodBoolean;

  return isPrimitiveType ? [prefix] : [];
};

export const removeEmptyFields = (data: any) => {
  return Object.fromEntries(
    Object.entries(data).filter(([_, value]) => value !== undefined)
  );
};

export const setEmptyFieldsToNull = (data: any) => {
  Object.keys(data).forEach((key) => {
    if (data[key] == "") {
      data[key] = null;
    }
  });
  return data;
};

export const sortByDate = (data: any, key: string) => {
  const a = new Date(data[key]).getTime();
  const b = new Date(data[key]).getTime();
  return a < b ? -1 : a > b ? 1 : 0;
};

export const formatAmount = (
  amount: number | string,
  maximumFractionDigits: number = 2,
  locale: string = "en-US",
  currency: string = "USD"
): string => {
  const numericAmount =
    typeof amount === "string" ? parseFloat(amount) : amount;

  if (isNaN(numericAmount)) return "$0";

  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency,
    maximumFractionDigits: maximumFractionDigits,
  }).format(numericAmount);
};

export const yearOptions = (length: number, prevYear: number = 2) => {
  return new Array(length)
    .fill(0)
    .map((_, index: number) => ({
      label: (moment().year() - prevYear + index).toString(),
      value: (moment().year() - prevYear + index).toString(),
    }))
    .reverse();
};

export const monthOptions = [
  { label: "All", value: "all" },
  ...moment.months().map((month, index) => ({
    label: month,
    value: (index + 1).toString().padStart(2, "0"),
  })),
];

export const ROLE_ADMIN = "ROLE_ADMIN";
export const ROLE_OWNER = "ROLE_OWNER";
export const ROLE_USER = "ROLE_USER";
export const ROLE_BOM = "ROLE_BOM";
export const ROLE_CAM = "ROLE_CAM";
export const ROLE_CAM_ACCOUNTANT = "ROLE_CAM_ACCOUNTANT";

export const roles = {
  ROLE_ADMIN: "Admin",
  ROLE_OWNER: "Owner",
  ROLE_BOM: "BM",
  ROLE_CAM: "CAM",
  ROLE_CAM_ACCOUNTANT: "CAM Accountant",
};

export const capitalize = (str: string): string => {
  if (!str) return "";
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};

export const formatTwoDigits = (num: number): string => {
  return num < 10 ? `0${num}` : `${num}`;
};

export const formatCurrencyShort = (value: number): string => {
  if (value >= 1_000_000_000) {
    return `$${(value / 1_000_000_000).toFixed(1)}B`;
  } else if (value >= 1_000_000) {
    return `$${(value / 1_000_000).toFixed(1)}M`;
  } else if (value >= 1_000) {
    return `$${(value / 1_000).toFixed(1)}K`;
  }
  return `$${value}`;
};

export const getFirstValue = (source: any, keys: string[]) => {
  for (const key of keys) {
    const value = source?.[key];

    if (value !== undefined && value !== null && value !== "") {
      return value;
    }
  }

  return "";
};

export const formatEntryMonthYear = (value?: string) => {
  if (!value) {
    return "";
  }

  const [month, year] = value.toString().split("/");

  if (month && year) {
    return `${month.padStart(2, "0")}/${year}`;
  }

  return value.toString();
};

export const calculatePercentageChange = (
  current: number,
  previous: number
): { value: string; progress: string } => {
  if (previous == 0) return { value: "100%", progress: "up" };
  const change = ((current - previous) / previous) * 100;
  const sign = change >= 0 ? "+" : "";

  return {
    value: change ? `${sign}${(change || 0).toFixed(2)}%` : "0",
    progress: change ? (change >= 0 ? "up" : "down") : "",
  };
};

export const invoiceParkStatus: any = {
  all: "",
  draft: {
    label: "Parked",
    color: "border-primary bg-blue-100 text-primary",
    status: "DRAFT",
  },
  pending_for_approval: {
    label: "Pending For Approval",
    status: "PENDING_APPROVAL",
    color: "border-yellow-600 bg-yellow-100 text-yellow-600",
  },
  pending_for_cam_approval: {
    label: "Pending For CAM Approval",
    color: "border-yellow-600 bg-yellow-100 text-yellow-600",
    status: "PENDING_APPROVAL",
    approvalStatus: "CAM:PENDING_APPROVAL",
  },
  pending_for_my_approval: {
    label: "Pending For My Approval",
    color: "border-yellow-600 bg-yellow-100 text-yellow-600",
    status: "PENDING_APPROVAL",
    approvalStatus: "BOM:PENDING_APPROVAL",
  },
  pending_for_board_approval: {
    label: "Pending For Board Approval",
    color: "border-yellow-600 bg-yellow-100 text-yellow-600",
    status: "PENDING_APPROVAL",
    approvalStatus: "BOARD:PENDING_APPROVAL",
  },
  pending_for_agm_approval: {
    label: "Pending For AGM Approval",
    color: "border-yellow-600 bg-yellow-100 text-yellow-600",
    status: "PENDING_APPROVAL",
    approvalStatus: "AGM:PENDING_APPROVAL",
  },
  pending_approval: {
    label: "Pending Approval",
    color: "border-yellow-600 bg-yellow-100 text-yellow-600",
    status: "PENDING_APPROVAL",
  },
  approved: {
    label: "Approved",
    color: "border-green-600 bg-green-100 text-green-600",
    status: "APPROVED",
  },
  rejected: {
    label: "Rejected",
    color: "border-red-600 bg-red-100 text-red-600",
    status: "REJECTED",
  },
  posted: {
    label: "Posted",
    color: "border-green-600 bg-green-100 text-green-600",
    status: "POSTED",
  },
};

export const billingHistoryStatus: any = {
  pending: {
    label: "Pending",
    color: "border-yellow-600 bg-yellow-100 text-yellow-600",
    status: "PENDING",
  },
  completed: {
    label: "Completed",
    color: "border-green-600 bg-green-100 text-green-600",
    status: "COMPLETED",
  },
};

export const cashAppStatus: any = {
  draft: {
    label: "Pending",
    color: "border-yellow-600 bg-yellow-100 text-yellow-600",
    status: "PENDING",
  },
  approved: {
    label: "Pending",
    color: "border-yellow-600 bg-yellow-100 text-yellow-600",
    status: "PENDING",
  },
  pending_approval: {
    label: "Pending",
    color: "border-yellow-600 bg-yellow-100 text-yellow-600",
    status: "PENDING",
  },
  posted: {
    label: "Posted",
    color: "border-green-600 bg-green-100 text-green-600",
    status: "POSTED",
  },
};

export const invoiceActionStatus = {
  all: "all",
  draft: "draft",
  pending_for_approval: "pending_for_approval",
  pending_for_cam_approval: "pending_for_cam_approval",
  pending_for_my_approval: "pending_for_my_approval",
  pending_for_board_approval: "pending_for_board_approval",
  pending_for_agm_approval: "pending_for_agm_approval",
  approved: "approved",
  rejected: "rejected",
  posted: "posted",
  reversed: "reversed",
};

export const paymentStatus: any = {
  all: "",
  posted: {
    label: "Posted Invoice Amount",
    status: "POSTED",
    color: "border-primary bg-blue-100 text-primary",
  },
  pending: {
    label: "Pending Invoice",
    status: "PENDING",
    color: "border-yellow-600 bg-yellow-100 text-yellow-600",
  },
  paid: {
    label: "Paid Invoice",
    status: "PAID",
    color: "border-green-600 bg-green-100 text-green-600",
  },
  unpaid: {
    label: "Unpaid",
    status: "UNPAID",
    color: "border-red-600 bg-red-100 text-red-600",
  },
};

export const distributionJobStatus: any = {
  all: "",
  scheduled: {
    label: "Scheduled",
    status: "SCHEDULED",
    color: "border-primary bg-blue-100 text-primary",
  },
  executed: {
    label: "Executed",
    status: "EXECUTED",
    color: "border-yellow-600 bg-yellow-100 text-yellow-600",
  },
  superseded: {
    label: "Replaced",
    status: "SUPERSEDED",
    color: "border-red-600 bg-red-100 text-red-600",
  },
  failed: {
    label: "Failed",
    status: "FAILED ",
    color: "border-red-600 bg-red-100 text-red-600",
  },
  posted: {
    label: "Posted",
    status: "POSTED",
    color: "border-green-600 bg-green-100 text-green-600",
  },
};

export const billingStatus: any = {
  all: "",
  billing_config: {
    label: "Billing Configuration",
    action: "billing_config",
  },
  pending_for_posting: {
    label: "Pending For Posting",
    action: "pending_for_posting",
  },
  billed_for_period: {
    label: "Billed For the Period",
    action: "billed_for_period",
  },
  total_collection: {
    label: "Total Collection",
    action: "total_collection",
  },
  billing_log: {
    label: "Billing Log",
    action: "billing_log",
  },
};

export const MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

// Access Management
export const rolesOptions = [
  { label: "CAM", value: "ROLE_CAM" },
  { label: "CAM-A", value: "ROLE_CAM_ACCOUNTANT" },
];

export type PermissionItem = {
  actions: string[];
  bomPositionId: number | null;
  communityId: number;
  createdAt: string;
  createdBy: number;
  maxPermission: number | null;
  permissionBits: number;
  permissionId: number;
  permissionScope: string;
  updatedAt: string;
  updatedBy: number;
  userRole: string;
};

export type ModulePermissions = Record<string, PermissionItem[]>;

export type TransformedPermissions = Record<
  string, // moduleName e.g. BILLING_CONFIG
  Record<
    string, // action e.g. EXPORT, VIEW
    {
      permissionId: number;
      userRole: string;
      communityId: number;
      permissionScope: string;
      permissionBits: number;
    }[]
  >
>;

// Key type
export type ModuleName = keyof typeof permissionMatrixTitles;

// Value type
export type ModulePermissionItem = { label: string; value: string };

export const transformPermissions = (data: any) => {
  const result: any = [];

  data.forEach((item: any) => {
    const { module, ...rest } = item;

    if (!result[module]) {
      result[module] = [];
    }

    result[module].push({ ...rest });
  });

  return result;
};

export const transformActions = (
  modules: ModulePermissions
): TransformedPermissions => {
  const result: TransformedPermissions = {};

  Object.entries(modules).forEach(([moduleName, items]) => {
    result[moduleName] = items.reduce<Record<string, any[]>>((acc, item) => {
      item?.actions?.forEach((action) => {
        if (!acc[action]) {
          acc[action] = [];
        }
        acc[action].push({
          ...item,
        });
      });
      return acc;
    }, {});
  });

  return result;
};

export const permissionRoles = {
  BOM_POSITION: "BOM_POSITION",
  ROLE_CAM: "ROLE_CAM",
  ROLE_CAM_ACCOUNTANT: "ROLE_CAM_ACCOUNTANT"
}
// Note: Keys like BUDGET and INVOICE must be in uppercase to match the API response.
// action values: CREATE, SUBMIT, APPROVE, READ, POST, READ_POSTED, UPDATE, DELETE
export const permissionMatrixTitles = {
  BUDGET: [
    {
      label: "View Budget",
      value: "READ",
    },
    {
      label: "Create Budget",
      value: "CREATE",
    },
    {
      label: "Revise Budget",
      value: "UPDATE",
    },
    {
      label: "Submit For Approval",
      value: "SUBMIT",
    },
    {
      label: "Approve Budget",
      value: "APPROVE",
      disabledRoles: [permissionRoles.ROLE_CAM, permissionRoles.ROLE_CAM_ACCOUNTANT]
    },
    {
      label: "Delete Budget",
      value: "DELETE",
    },
  ],
  INVOICE: [
    {
      label: "View Invoice",
      value: "READ",
    },
    {
      label: "Create Invoice",
      value: "CREATE",
    },
    {
      label: "Submit For Approval",
      value: "SUBMIT",
    },
    {
      label: "Approve Invoice",
      value: "APPROVE",
    },
    {
      label: "Post Invoice",
      value: "POST",
    },
    {
      label: "View Posted Invoice",
      value: "READ_POSTED",
    },
  ],
  PAYMENT: [
    {
      label: "View Payment Document",
      value: "READ",
    },
    {
      label: "Create Payment Document",
      value: "CREATE",
    },
    {
      label: "Edit Payment Document",
      value: "UPDATE",
    },
    {
      label: "Post Payment Document",
      value: "POST",
    },
  ],
  BILLING_CONFIG: [
    {
      label: "View Billing Configuration",
      value: "READ",
    },
    {
      label: "Add Billing Configuration",
      value: "CREATE",
    },
  ],
  BILLING_LOGS: [
    {
      label: "View Billing Logs",
      value: "READ",
    },
  ],
  BILLING_DOCUMENT: [
    {
      label: "View Billing Document",
      value: "READ",
    },
    {
      label: "Edit Billing Document",
      value: "UPDATE",
    },
    {
      label: "Delete Billing Document",
      value: "DELETE",
    },
    {
      label: "Post Billing Document",
      value: "POST",
    },
  ],
  DOCUMENT_TYPE: [
    {
      label: "Add Document Type",
      value: "CREATE",
      disabledRoles: [permissionRoles.BOM_POSITION]
    },
    {
      label: "Edit Document Type",
      value: "UPDATE",
      disabledRoles: [permissionRoles.BOM_POSITION]
    },
    // {
    //   label: "Delete Document Type",
    //   value: "DELETE",
    //   disabledRoles: [permissionRoles.BOM_POSITION]
    // },
  ],
  UNIFIED_JE: [
    {
      label: "View JE",
      value: "READ",
    },
    {
      label: "Add JE",
      value: "CREATE",
    },
    {
      label: "Post JE",
      value: "POST",
    },
  ],
  INCIDENT_MANAGEMENT: [
    {
      label: "Add Incident",
      value: "CREATE",
    },
    {
      label: "Edit Incident",
      value: "UPDATE",
    },
    // {
    //   label: "Delete Incident",
    //   value: "DELETE",
    //   disabledRoles: [permissionRoles.BOM_POSITION, permissionRoles.ROLE_CAM, permissionRoles.ROLE_CAM_ACCOUNTANT]
    // },
  ],
  CASH_APP: [
    {
      label: "Add Cash App",
      value: "CREATE",
    },
    {
      label: "View Cash App",
      value: "READ",
    },
    {
      label: "Post Cash App",
      value: "POST",
    },
  ],
  MANUAL_CLEARING: [
    {
      label: "View Manual Clearing",
      value: "READ",
    },
    {
      label: "Add Manual Clearing",
      value: "CREATE",
    },
    {
      label: "Post Manual Clearing",
      value: "POST",
    },
  ],
  GL_TRIAL_BALANCE: [
    {
      label: "View GL Trial Balance Report",
      value: "READ",
    }
  ],
  FISCAL_YEAR: [
    {
      label: "View Fiscal Year Close Dashboard",
      value: "READ"
    },
    {
      label: "Edit Fiscal Year Close Dashboard",
      value: "UPDATE"
    }
  ],
  FISCAL_YEAR_LIFE_CYCLE: [
    {
      label: "View Fiscal Year Life Cycle Dashboard",
      value: "READ"
    },
    {
      label: "Edit Fiscal Year Life Cycle Dashboard",
      value: "UPDATE"
    }
  ],
  // MANUAL_JE: [
  //   {
  //     label: "View JE Manual",
  //     value: "READ",
  //   },
  //   {
  //     label: "Add JE Manual",
  //     value: "CREATE",
  //     disabledRoles: [permissionRoles.BOM_POSITION]
  //   },
  //   {
  //     label: "Post JE Manual",
  //     value: "POST",
  //   },
  // ],
  AUDIT_TRAIL: [
    {
      label: "View Audit Trail",
      value: "READ"
    },
    {
      label: "Edit Audit Trail",
      value: "UPDATE"
    }
  ],
  DISTRIBUTION_REPORT: [
    {
      label: "View Allocation Report",
      value: "READ"
    }
  ],
  DISTRIBUTION_CONFIG: [
    {
      label: "View Allocation Config",
      value: "READ"
    },
    {
      label: "Add Allocation Config",
      value: "CREATE"
    },
    {
      label: "Edit Allocation Config",
      value: "UPDATE"
    }
  ],
  GL_CONFIG: [
    {
      label: "View GL Configuration",
      value: "READ"
    },
    {
      label: "Edit GL Configuration",
      value: "UPDATE"
    }
  ],
  COLLECTION_REPORT: [
    {
      label: "View Collection Report",
      value: "READ",
    }
  ],
  INVOICE_REPORT: [
    {
      label: "View Invoice Report",
      value: "READ",
    }
  ],
  OWNER_BALANCE_REPORT: [
    {
      label: "View Owner Balance Report",
      value: "READ",
    }
  ],
  PAYMENT_REPORT: [
    {
      label: "View Payment Report",
      value: "READ",
    }
  ],
  TOLERANCE_REPORT: [
    {
      label: "View Tolerance Report",
      value: "READ",
    }
  ],
  TRACEABILITY_BALANCE: [
    {
      label: "View Traceability Report",
      value: "READ",
    }
  ],
  VENDOR_BALANCE_REPORT: [
    {
      label: "View Vendor Balance Report",
      value: "READ",
    }
  ],
  COST_ANALYTICS_TRIAL_BALANCE: [
    {
      label: "View Cost Analytics Trial Balance Report",
      value: "READ",
    }
  ],
  OWNER_TRIAL_BALANCE: [
    {
      label: "View Owner Trial Balance Report",
      value: "READ",
    }
  ],
  VENDOR_TRIAL_BALANCE: [
    {
      label: "View Vendor Trial Balance Report",
      value: "READ",
    }
  ],
  CLOSEOUT_BALANCE: [
    {
      label: "View Closeout Report",
      value: "READ",
    }
  ],
  RECONCILIATION_REPORT: [
    {
      label: "View GL/CA Reconciliation Report",
      value: "READ",
    }
  ],
  DISPLAY_ACCOUNTING_DOCUMENT: [
    {
      label: "View - Display Accounting Document Report",
      value: "READ",
    }
  ],
  MASTER_DATA: [
    {
      label: "View Master Data",
      value: "READ",
    },
    {
      label: "Add Master Data",
      value: "CREATE",
      disabledRoles: [permissionRoles.BOM_POSITION]
    },
    {
      label: "Delete Master Data",
      value: "DELETE",
      disabledRoles: [permissionRoles.BOM_POSITION]
    }
  ]
} as const;

export const actionsKeys = Object.keys(permissionMatrixTitles).reduce((acc, key) => {
  acc[key] = key; // Or acc[key] = { value: key };
  return acc;
}, {} as Record<string, any>);

export const permissionModules = [
  { 
    key: "auditTrail",
    label: "Audit Trail",
    icon: File,
    children: [actionsKeys.AUDIT_TRAIL]
  },
  {
    key: "billingConfig",
    label: "Billing Config",
    icon: File,
    children: [actionsKeys.BILLING_CONFIG]
  },
  {
    key: "billingDoc",
    label: "Billing Document",
    icon: File,
    children: [actionsKeys.BILLING_DOCUMENT]
  },
  {
    key: "billingLogs",
    label: "Billing Logs",
    icon: File,
    children: [actionsKeys.BILLING_LOGS]
  },
  {
    key: "budget",
    label: "Budget",
    icon: File,
    children: [actionsKeys.BUDGET]
  },
  {
    key: "cashApp",
    label: "Cash App",
    icon: File,
    children: [actionsKeys.CASH_APP]
  },
  {
    key: "distRule",
    label: "Allocation Rule",
    icon: File,
    children: [actionsKeys.DISTRIBUTION_CONFIG]
  },
  {
    key: "docType",
    label: "Document Type",
    icon: File,
    children: [actionsKeys.DOCUMENT_TYPE]
  },
  {
    key: "generalLedger",
    label: "General Ledger",
    icon: File,
    children: [
      actionsKeys.GL_CONFIG,
      actionsKeys.FISCAL_YEAR,
      actionsKeys.FISCAL_YEAR_LIFE_CYCLE,
      // actionsKeys.MANUAL_JE,
      actionsKeys.DISPLAY_ACCOUNTING_DOCUMENT
    ]
  },
  {
    key: "incident",
    label: "Incident Management",
    icon: File,
    children: [actionsKeys.INCIDENT_MANAGEMENT]
  },
  {
    key: "invoice",
    label: "Invoice",
    icon: File,
    children: [actionsKeys.INVOICE]
  },
  {
    key: "manualClearing",
    label: "Manual Clearing",
    icon: File,
    children: [actionsKeys.MANUAL_CLEARING]
  },
  {
    key: "payment",
    label: "Payment",
    icon: File,
    children: [actionsKeys.PAYMENT]
  },
  {
    key: "reports",
    label: "Reports",
    icon: File,
    children: [
      actionsKeys.INVOICE_REPORT,
      actionsKeys.VENDOR_BALANCE_REPORT,
      actionsKeys.OWNER_BALANCE_REPORT,
      actionsKeys.GL_TRIAL_BALANCE,
      actionsKeys.PAYMENT_REPORT,
      actionsKeys.TOLERANCE_REPORT,
      actionsKeys.TRACEABILITY_BALANCE,
      actionsKeys.COLLECTION_REPORT,
      actionsKeys.DISTRIBUTION_REPORT,
      actionsKeys.COST_ANALYTICS_TRIAL_BALANCE,
      actionsKeys.OWNER_TRIAL_BALANCE,
      actionsKeys.VENDOR_TRIAL_BALANCE,
      actionsKeys.CLOSEOUT_BALANCE,
      actionsKeys.RECONCILIATION_REPORT
    ]
  },
  {
    key: "masterData",
    label: "Master Data",
    icon: File,
    children: [actionsKeys?.MASTER_DATA]
  },
];

export const getPermissionLabel = (
  moduleName: ModuleName,
  action: string
): string | undefined => {
  return permissionMatrixTitles[moduleName]?.find(
    (val: ModulePermissionItem) => val.value === action
  )?.label;
};

export const permissionActions = {
  CREATE: "CREATE",
  UPDATE: "UPDATE",
  DELETE: "DELETE",
  READ: "READ",
  READ_POSTED: "READ_POSTED",
  POST: "POST",
  SUBMIT: "SUBMIT",
  APPROVE: "APPROVE",
};

export const mapActionsToBooleans = (allowed: string[]) => {
  return Object.fromEntries(
    Object.values(permissionActions).map((action) => [
      action,
      allowed?.includes(action),
    ])
  );
};

// ---------------------------------------------------------------------------
export const convertedFilterData = (filterData: any) => {
  if (!filterData) return null;
  return Object.entries(filterData)
    .filter(
      ([_, value]) =>
        value !== null &&
        value !== undefined &&
        (!Array.isArray(value) || value.length > 0)
    )
    .flatMap(([key, value]) =>
      Array.isArray(value)
        ? value.map((v) => ({
            field: key,
            value: v,
            operator: "EQUAL",
          }))
        : [
            {
              field: key,
              value,
              operator: "EQUAL",
            },
          ]
    );
};

export const transactionTypeValues = {
  ANNUAL_HOA_FEE: "Annual",
  MONTHLY_HOA_FEE: "Monthly",
  ADHOC_HOA_FEE: "Adhoc",
};

export const incidentStatus = {
  reported: {
    label: "Reported",
    value: "REPORTED",
    color: "border-blue-600 bg-blue-100 text-blue-600",
  },
  open: {
    label: "Open",
    value: "OPEN",
    color: "border-amber-600 bg-amber-100 text-amber-600",
  },
  investigating: {
    label: "Investigating",
    value: "INVESTIGATING",
    color: "border-green-600 bg-green-100 text-green-600",
  },
  awaiting_response: {
    label: "Awaiting Response",
    value: "AWAITING_RESPONSE",
    color: "border-green-600 bg-green-100 text-green-600",
  },
  mentor_assigned: {
    label: "Mentor Assigned",
    value: "MENTOR_ASSIGNED",
    color: "border-purple-600 bg-purple-100 text-purple-600",
  },
  sme_assigned: {
    label: "SME Assigned",
    value: "SME_ASSIGNED",
    color: "border-indigo-600 bg-indigo-100 text-indigo-600",
  },
  owner_review: {
    label: "Owner Review",
    value: "OWNER_REVIEW",
    color: "border-yellow-600 bg-yellow-100 text-yellow-600",
  },
  final_review: {
    label: "Final Review",
    value: "FINAL_REVIEW",
    color: "border-orange-600 bg-orange-100 text-orange-600",
  },
  charged_closed: {
    label: "Charged & Closed",
    value: "CHARGED_CLOSED",
    color: "border-gray-600 bg-gray-100 text-gray-600",
  },
  citation_closed: {
    label: "Citation Closed",
    value: "CITATION_CLOSED",
    color: "border-gray-600 bg-gray-100 text-gray-600",
  },
} as const;

export const getBaseRoute = (path: string) =>
  path?.split("/")?.filter(Boolean)?.[0] || "";

export const executionStatus = {
  inactive: {
    label: "Inactive",
    value: "IN_ACTIVE",
    color: "border-red-600 bg-red-100 text-red-600",
  },
  pending: {
    label: "Pending",
    value: "PENDING",
    color: "border-gray-600 bg-gray-100 text-gray-600",
  },
  past_due: {
    label: "Past Due",
    value: "PAST_DUE",
    color: "border-red-600 bg-red-100 text-red-600",
  },
  scheduled: {
    label: "Scheduled",
    value: "SCHEDULED",
    color: "border-indigo-600 bg-indigo-100 text-indigo-600",
  },
  running: {
    label: "Running",
    value: "RUNNING",
    color: "border-blue-600 bg-blue-100 text-blue-600",
  },
  completed: {
    label: "Completed",
    value: "COMPLETED",
    color: "border-green-600 bg-green-100 text-green-600",
  },
  failed: {
    label: "Failed",
    value: "FAILED",
    color: "border-red-600 bg-red-100 text-red-600",
  },
  retry: {
    label: "Retry",
    value: "RETRY",
    color: "border-amber-600 bg-amber-100 text-amber-600",
  },
  cancelled: {
    label: "Inactive",
    value: "CANCELLED",
    color: "border-gray-600 bg-gray-300 text-gray-700",
  },
  skipped: {
    label: "Skipped",
    value: "SKIPPED",
    color: "border-slate-600 bg-slate-100 text-slate-600",
  },
} as const;

const getOrdinalSuffix = (day: number) => {
  if (day >= 11 && day <= 13) return "th";
  switch (day % 10) {
    case 1:
      return "st";
    case 2:
      return "nd";
    case 3:
      return "rd";
    default:
      return "th";
  }
};

export const getMonthlyDayOptions = (day: number) =>
  `${day}${getOrdinalSuffix(day)} of every month`;

export const stateLawStatus = {
  published: {
    label: "Published",
    value: "PUBLISHED",
    color: "border-green-600 bg-green-100 text-green-600",
  },
  draft: {
    label: "Draft",
    value: "DRAFT",
    color: "border-red-600 bg-red-100 text-red-600",
  },
};

export const POSTING_YEAR = "postingYear";
export const BUDGET_YEAR = "budgetYear";

export const accountCategoryOptions = [
  { label: "Assets", value: "A" },
  { label: "Contra Asset", value: "CA" },
  { label: "Contra Liability", value: "CL" },
  { label: "Contra Revenue", value: "CR" },
  { label: "Expense", value: "E" },
  { label: "Fund Balance", value: "FB" },
  { label: "Income", value: "I" },
  { label: "Liabilities", value: "L" },
  { label: "Retained Earnings", value: "R" },
  { label: "None", value: "NONE" },
];

export const subledgerOptions = [
  { label: "Customer Accounts", value: "C" },
  { label: "Vendor Accounts", value: "V" },
  { label: "None", value: "NONE" },
];

export const subledgerTypeOptions = [
  { label: "Accounts Receivable Recon", value: "ACCOUNTS_RECEIVABLE_RECON" },
  { label: "Accounts Payable Recon", value: "ACCOUNTS_PAYABLE_RECON" },
  { label: "None", value: "NONE" },
];

export const transactionTypeOptions = [
  {
    label: "Yearly HOA Fee",
    value: "ANNUAL_HOA_FEE",
  },
  {
    label: "Monthly HOA Fee",
    value: "MONTHLY_HOA_FEE",
  },
  {
    label: "Adhoc HOA Fee",
    value: "ADHOC_HOA_FEE",
  },
];

export const documentTypeOptions = [
  { label: "JE Journal Entry", value: "JE" },
  { label: "JX JE Reversal", value: "JX" },
  { label: "GL Clearing", value: "GL" },
  { label: "GX Reverse GL Clearing", value: "GX" },
  { label: "FF Carry Forward (Future)", value: "FF" },
  { label: "FX Reverse Carry Forward (Future)", value: "FX" },
  { label: "EJ Electronic Bank JE Upload", value: "EJ" },
  { label: "EI Electronic Bank Interface", value: "EI" },
  { label: "EX Electronic Bank Reversal", value: "EX" },
  { label: "EM Electronic Manual Receipt", value: "EM" },
  { label: "EY Eelect Man Rec Reversal", value: "EY" },
  { label: "OB Billing Engine", value: "OB" },
  { label: "OX Revenue Accrual Reversal", value: "OX" },
  { label: "OM Manual Billing", value: "OM" },
  { label: "OY Manual Billing Reversal", value: "OY" },
  { label: "OJ Cash App - JE Upload", value: "OJ" },
  { label: "OI Cash App - Interface", value: "OI" },
  { label: "OZ OZ Cash App Reversal", value: "OZ" },
  { label: "OA Cash App - Manual", value: "OA" },
  { label: "OW Cash App M Reversal", value: "OW" },
  { label: "OC Owner Clearing", value: "OC" },
  { label: "OV Owner Clearing Reversal", value: "OV" },
  { label: "VI Vendor Invoice", value: "VI" },
  { label: "VX Invoice Reversal", value: "VX" },
  { label: "VM Manual Invoice", value: "VM" },
  { label: "VY Manual Inv Reversal", value: "VY" },
  { label: "VA Auto Payment", value: "VA" },
  { label: "VP Manual Payment", value: "VP" },
  { label: "VZ Payment Reversal", value: "VZ" },
  { label: "VC Vendor Clearing", value: "VC" },
  { label: "VW Vendor Clearing Reversal", value: "VW" },
];

export const transactionalProcessorOptions = [
  { label: "JE Journal Entry", value: "JE" },
  { label: "EB Electronic Banking", value: "EB" },
  { label: "CB Customer Billing", value: "CB" },
  { label: "CA Cash Application", value: "CA" },
  { label: "CC Customer Clearing", value: "CC" },
  { label: "VI Vendor Invoice", value: "VI" },
  { label: "VP Vendor Payment", value: "VP" },
  { label: "VC Vendor Clearing", value: "VC" },
];

export const drcrOptions = [
  { label: "Dr", value: "DR" },
  { label: "Cr", value: "CR" },
];

export const processingStatusList: any = {
  "VALIDATION_FAILED": {
    label: "Failed",
    className: 'text-red-600'
  },
  "VALIDATION_PASSED": {
    label: "Passed",
    className: 'text-green-600'
  },
  "FAILED": {
    label: "Failed",
    className: 'text-red-600'
  },
  "PASSED": {
    label: "Passed",
    className: 'text-green-600'
  },
  "POSTED": {
    label: "Posted",
    className: 'text-green-600'
  },
  "PARKED": {
    label: "Parked",
    className: 'text-blue-600'
  },
  "AWAITING_APPROVAL": {
    label: "Pending Approval",
    className: 'text-amber-600'
  }
}

export const roleMap: any = {
  ROLE_BOM: "ROLE_BOARD_MEMBER",
  ROLE_OWNER: "ROLE_OWNER"
}

export const helpDocAudience = {
  BOARD_MEMBER: "Board Member",
  OWNER: "Owner"
} as const;

export const reportingLevelOptions = [
  { label: "Community", value: "COMMUNITY" },
  { label: "Zone", value: "ZONE" },
  { label: "Block", value: "BLOCK" },
  { label: "Property", value: "PROPERTY" },
];

export const COMMUNITY_TO_ZONE = "COMMUNITY_TO_ZONE";
export const ZONE_TO_BLOCK = "ZONE_TO_BLOCK";
export const BLOCK_TO_PROPERTY = "BLOCK_TO_PROPERTY";

export const levels = {
  COMMUNITY_TO_ZONE: "Community To Zone",
  ZONE_TO_BLOCK: "Zone To Block",
  BLOCK_TO_PROPERTY: "Block To Property"
}

export const distributionLevels: any = {
  EQUAL_DISTRIBUTION: "Equal Distribution",
  FIXED_WEIGHTS: "Fixed Weights",
  SQUARE_FOOTAGE: "Square Footage",
  WEIGHING_FACTOR: "Weighing Factor",
  WEIGHING_FACTORS: "Weighing Factor"
}

export const activeLevels = [
  {
    label: 'Community → Zone',
    value: 'ZONE',
    level: 'COMMUNITY_TO_ZONE',
    source: 'Community',
    receiver: 'Zone'
  },
  {
    label: 'Zone → Block',
    value: 'BLOCK',
    level: 'ZONE_TO_BLOCK',
    source: 'Zone',
    receiver: 'Block'
  },
  {
    label: 'Block → Property',
    value: 'PROPERTY',
    level: 'BLOCK_TO_PROPERTY',
    source: 'Block',
    receiver: 'Property'
  },
]

export const PLANNED = "PLAN";
export const ACTUALS = "ACTUAL";
export const FIXED_WEIGHTS = "FIXED_WEIGHTS";
export const SQUARE_FOOTAGE = "SQUARE_FOOTAGE";
export const WEIGHING_FACTOR = "WEIGHING_FACTOR"; // metric type
export const WEIGHING_FACTORS = "WEIGHING_FACTORS"; // distribution method

export const plannedTab = [
  {
    label: "Planned",
    value: PLANNED,
  }
];

export const actualTab = [
  {
    label: "Actuals",
    value: ACTUALS,
  },
];

export const clearingStatusOptions = [
  {
    label: 'All',
    value: 'ALL'
  },
  {
    label: 'Paid',
    value: 'PAID'
  },
  {
    label: 'Unpaid',
    value: 'UNPAID'
  },
]

export const allOption = [
  {label: "All", value: "all"}
]

export const fileTypeOptions = [
  {label: 'PDF', value: "PDF"},
  {label: 'JPEG', value: "JPEG"},
  {label: 'PNG', value: "PNG"},
  {label: 'XLS', value: "XLS"},
  {label: 'XLSX', value: "XLSX"},
  {label: 'DOC', value: "DOC"},
  {label: 'DOCX', value: "DOCX"},
  {label: 'CSV', value: "CSV"},
];

export const ocpStageOption = [
  {label: 'Closing Proof', value: "CLOSING_PACKAGE"},
  {label: 'Owner Onboarding', value: "OWNER_ONBOARDING"},
  {label: 'Ownership Change', value: "OWNERSHIP_CHANGE_DOC"},
]

export const INVOICE_DOC_TYPES = ["VI"];
export const PAYMENT_DOC_TYPES = ["VP"];

export const toApiSort = (
  sorting?: Array<{ id: string; desc: boolean }>,
  fallback?: { field: string; direction: "ASC" | "DESC" }
) => {
  if (sorting?.length) {
    return sorting.map((item) => ({
      field: item.id,
      direction: (item.desc ? "DESC" : "ASC") as "ASC" | "DESC",
    }));
  }
  return fallback ? [fallback] : [];
};

export const parseRelatedDocuments = (relatedDocuments: string[] = []) => {
  const linkedInvoiceIds: string[] = [];
  const linkedPaymentIds: string[] = [];

  relatedDocuments?.forEach((item: string) => {
    const [id, type] = String(item || "").split(":");
    if (!id?.trim() || !type) return;
    const docType = type.trim().toUpperCase();
    if (INVOICE_DOC_TYPES.includes(docType)) {
      linkedInvoiceIds.push(id.trim());
    } else if (PAYMENT_DOC_TYPES.includes(docType)) {
      linkedPaymentIds.push(id.trim());
    }
  });

  return { linkedInvoiceIds, linkedPaymentIds };
};

export const formatPhoneNumber = (input: string): string => {
  console.log("🚀 ~ formatPhoneNumber ~ input:", input)
  if (!input) return "";

  // 1. Trim whitespace
  let cleaned = input.trim();

  // 2. Handle international '00' prefix if present (e.g. 001... -> +1...)
  if (cleaned.startsWith("00")) {
    cleaned = "+" + cleaned.slice(2);
  }

  // 3. Check if it originally started with a '+'
  const hasPlus = cleaned.startsWith("+");

  // 4. Remove all non-digit characters
  const digitsOnly = cleaned.replace(/\D/g, "");

  // 5. Rebuild with a '+' if it had one, or prepend '+' if it's a full international number without a '+'
  let formatted = "";
  if (hasPlus) {
    formatted = "+" + digitsOnly;
  } else if (digitsOnly.length >= 7) {
    // Assuming numbers without '+' but sufficient length imply a country code
    formatted = "+" + digitsOnly;
  } else {
    formatted = digitsOnly;
  }

  return formatted;
};

const VALIDATE_USER_FIELD_LABELS: Record<string, string> = {
  userName: "User ID",
  email: "Email ID 1",
  email2: "Email ID 2",
  phoneNumber: "Mobile No",
  loginId: "Alternative Login ID",
};

export const compactValidateUserParams = (params: Record<string, any> = {}) =>
  Object.fromEntries(
    Object.entries(params).filter(([, value]) => {
      if (value === undefined || value === null) return false;
      return String(value).trim() !== "";
    })
  );

export const formatValidateUserDataErrors = (checks: any[] = []) => {
  const messages = checks
    .filter((check) => check && check.available === false)
    .map((check) => {
      if (typeof check.message === "string" && check.message.trim()) {
        return check.message.trim();
      }
      const label = VALIDATE_USER_FIELD_LABELS[check.field] || check.field;
      return `${label} is already exists.`;
    });

  return messages
    .map((message) => (message.endsWith(".") ? message : `${message}.`))
    .join(" ");
};

export const getDuplicateNewUserFieldChecks = (
  incoming: Record<string, any> = {},
  users: any[] = [],
  excludeRowIndex?: string | number
) => {
  const pick = (u: any = {}) => {
    const s = { ...(u.newUser || {}), ...u };
    return { userName: s.userName ?? s.username, email: s.email, email2: s.email2, phoneNumber: s.phoneNumber, loginId: s.loginId };
  };
  const norm = (field: string, v: any) => {
    const val = String(v ?? "").trim().toLowerCase();
    return field === "phoneNumber" ? val.replace(/\D/g, "") : val;
  };
  const exclude = excludeRowIndex || excludeRowIndex === 0 ? String(excludeRowIndex) : undefined;

  return Object.entries(compactValidateUserParams(pick(incoming))).map(([field, value]) => ({
    field,
    available: !(users || []).some((user, i) => {
      if (!user || user.delete || (exclude && String(user.rowIndex ?? i) === exclude)) return false;
      const other: any = pick(user);
      const needle = norm(field, value);
      return field === "email" || field === "email2"
        ? [other.email, other.email2].some((v) => norm(field, v) === needle)
        : norm(field, other[field]) === needle;
    }),
  }));
};